export type IndustryProfileCode = 'FS' | 'TECH' | 'HEALTH' | 'CONST' | 'GEN';

export interface TerminologyProfile {
  // A2.2: Core Document & Section Labels
  doc_cmda_title: string;
  doc_cma_title: string;
  section_cda: string;
  section_mda: string;
  section_cda_questions: string;
  section_mda_questions: string;
  section_cma_control: string;
  section_cma_monitoring: string;

  // A2.3: Obligation / Requirement Field Labels
  req_field_label: string;
  req_field_placeholder: string;
  req_section_label: string;
  req_key_control_q: string;
  req_source_hint: string;
  req_input_mode: string;
  req_hidden: boolean; // true for GEN profile where obligation fields are hidden

  // A2.4: Role & Performer Labels
  performer_label: string;
  monitor_label: string;
  facilitator_label: string;
  sod_label: string;

  // A2.5: Scope & Population Labels
  product_label: string;
  product_q_label: string;
  segment_label: string;
  channel_label: string;

  // A2.6: Evidence & Execution Labels
  evidence_label: string;
  evidence_types_hint: string;
  instructions_label: string;
  escalation_label: string;

  // A2.7: Rating & Outcome Labels
  rating_adequate: string;
  rating_inadequate: string;
  issue_label: string;
  threshold_label: string;
  monitoring_type_default: string;
}

export const TERMINOLOGY_MAP: Record<IndustryProfileCode, TerminologyProfile> = {
  FS: {
    // A2.2
    doc_cmda_title: 'Control & Monitoring Design Assessment',
    doc_cma_title: 'Control Monitoring Assessment',
    section_cda: 'Control Design Assessment',
    section_mda: 'Monitoring Design Assessment',
    section_cda_questions: 'Control Design Adequacy',
    section_mda_questions: 'Monitoring Effectiveness',
    section_cma_control: 'Control Assessment',
    section_cma_monitoring: 'Monitoring Assessment',
    // A2.3
    req_field_label: 'Obligation',
    req_field_placeholder: 'Enter obligation ID and description',
    req_section_label: 'Regulations / Obligations related to Control',
    req_key_control_q: 'Is this a Key Control for each mapped Obligation?',
    req_source_hint: 'e.g. Reg E \u00A71005.34, MiFID II Art.16, GDPR Art.32',
    req_input_mode: 'Free text entry \u2013 assessor types/pastes obligation ID and description',
    req_hidden: false,
    // A2.4
    performer_label: 'Control Performer',
    monitor_label: 'Monitor',
    facilitator_label: 'Facilitator (Design Assessor)',
    sod_label: 'Segregation of duties',
    // A2.5
    product_label: 'Product',
    product_q_label: 'Does the Control cover all applicable products?',
    segment_label: 'Customer segment',
    channel_label: 'Channel',
    // A2.6
    evidence_label: 'Control evidence',
    evidence_types_hint: 'Audit log, call recording, approval record, system output, exception report',
    instructions_label: 'Procedures / playbook',
    escalation_label: 'Escalation process',
    // A2.7
    rating_adequate: 'Adequate',
    rating_inadequate: 'Inadequate',
    issue_label: 'Issue / Incident',
    threshold_label: 'Breach / Concern / Acceptable bands',
    monitoring_type_default: 'Metrics-type (detect from description)',
  },

  TECH: {
    // A2.2
    doc_cmda_title: 'Control & Monitoring Design Assessment',
    doc_cma_title: 'Control Monitoring Assessment',
    section_cda: 'Control Design Assessment',
    section_mda: 'Monitoring Design Assessment',
    section_cda_questions: 'Control Design Adequacy',
    section_mda_questions: 'Monitoring Effectiveness',
    section_cma_control: 'Control Assessment',
    section_cma_monitoring: 'Monitoring Assessment',
    // A2.3
    req_field_label: 'Framework clause',
    req_field_placeholder: 'Enter framework clause ID and description',
    req_section_label: 'Framework clauses related to Control',
    req_key_control_q: 'Is this a Key Control for each mapped Framework clause?',
    req_source_hint: 'e.g. SOC 2 CC6.1, ISO 27001 A.9.2, NIST AC-2',
    req_input_mode: 'Free text entry \u2013 assessor types/pastes clause ID and description',
    req_hidden: false,
    // A2.4
    performer_label: 'Control Owner',
    monitor_label: 'Monitor',
    facilitator_label: 'Facilitator (Design Assessor)',
    sod_label: 'Independence check',
    // A2.5
    product_label: 'System / module',
    product_q_label: 'Does the Control cover all applicable systems / modules?',
    segment_label: 'User tier / tenant',
    channel_label: 'Access method',
    // A2.6
    evidence_label: 'Audit artefact',
    evidence_types_hint: 'Access log, SIEM alert, pen test result, config screenshot, change ticket',
    instructions_label: 'Runbook / playbook',
    escalation_label: 'Escalation / incident process',
    // A2.7
    rating_adequate: 'Effective',
    rating_inadequate: 'Ineffective',
    issue_label: 'Finding',
    threshold_label: 'SLA / error rate threshold',
    monitoring_type_default: 'Either (detect from description)',
  },

  HEALTH: {
    // A2.2
    doc_cmda_title: 'Control & Monitoring Design Assessment',
    doc_cma_title: 'Control Monitoring Assessment',
    section_cda: 'Control Design Assessment',
    section_mda: 'Monitoring Design Assessment',
    section_cda_questions: 'Control Design Adequacy',
    section_mda_questions: 'Monitoring Effectiveness',
    section_cma_control: 'Control Assessment',
    section_cma_monitoring: 'Monitoring Assessment',
    // A2.3
    req_field_label: 'Regulatory requirement',
    req_field_placeholder: 'Enter regulatory requirement ID and description',
    req_section_label: 'Regulatory requirements related to Control',
    req_key_control_q: 'Is this a Key Control for each mapped Regulatory requirement?',
    req_source_hint: 'e.g. HIPAA \u00A7164.312, CQC Regulation 17, JCI Std 3',
    req_input_mode: 'Free text entry \u2013 assessor types/pastes requirement ID and description',
    req_hidden: false,
    // A2.4
    performer_label: 'Control Practitioner',
    monitor_label: 'Quality Assurance Lead',
    facilitator_label: 'Facilitator (Design Assessor)',
    sod_label: 'Independence check',
    // A2.5
    product_label: 'Care pathway / service',
    product_q_label: 'Does the Control cover all applicable care pathways / services?',
    segment_label: 'Patient segment',
    channel_label: 'Care setting / channel',
    // A2.6
    evidence_label: 'Clinical record',
    evidence_types_hint: 'Patient consent form, clinical audit trail, staff credential certificate, incident report',
    instructions_label: 'Clinical protocol / SOP',
    escalation_label: 'Escalation / notifiable incident process',
    // A2.7
    rating_adequate: 'Compliant',
    rating_inadequate: 'Non-compliant',
    issue_label: 'Non-conformance',
    threshold_label: 'Clinical outcome target',
    monitoring_type_default: 'Metrics-type (detect from description)',
  },

  CONST: {
    // A2.2
    doc_cmda_title: 'Control & Monitoring Design Assessment',
    doc_cma_title: 'Control Monitoring Assessment',
    section_cda: 'Control Design Assessment',
    section_mda: 'Monitoring Design Assessment',
    section_cda_questions: 'Control Design Adequacy',
    section_mda_questions: 'Monitoring Effectiveness',
    section_cma_control: 'Control Assessment',
    section_cma_monitoring: 'Monitoring Assessment',
    // A2.3
    req_field_label: 'Safety / quality standard',
    req_field_placeholder: 'Enter standard ID and description',
    req_section_label: 'Standards related to Control',
    req_key_control_q: 'Is this a Key Control for each mapped Standard?',
    req_source_hint: 'e.g. OSHA 1926.502, ISO 9001 \u00A78.5, ISO 45001 \u00A78.1',
    req_input_mode: 'Free text entry \u2013 assessor types/pastes standard ID and description',
    req_hidden: false,
    // A2.4
    performer_label: 'Control Operator',
    monitor_label: 'Clerk of Works / QA Lead',
    facilitator_label: 'Facilitator (Design Assessor)',
    sod_label: 'Independence check',
    // A2.5
    product_label: 'Work package / scope',
    product_q_label: 'Does the Control cover all applicable work packages / scope?',
    segment_label: 'Project type',
    channel_label: 'Site / location type',
    // A2.6
    evidence_label: 'Inspection record',
    evidence_types_hint: 'Site inspection sign-off, test certificate, safety checklist, method statement',
    instructions_label: 'Method statement / safety plan',
    escalation_label: 'Escalation / non-conformance process',
    // A2.7
    rating_adequate: 'Conformant',
    rating_inadequate: 'Non-conformant',
    issue_label: 'Defect / NCR',
    threshold_label: 'Defect rate / safety threshold',
    monitoring_type_default: 'Either (detect from description)',
  },

  GEN: {
    // A2.2 - matches current hardcoded labels
    doc_cmda_title: 'Control & Monitoring Design Assessment',
    doc_cma_title: 'Control Monitoring Assessment',
    section_cda: 'Control Design Assessment',
    section_mda: 'Monitoring Design Assessment',
    section_cda_questions: 'Control Design Adequacy',
    section_mda_questions: 'Monitoring Effectiveness',
    section_cma_control: 'Control Assessment',
    section_cma_monitoring: 'Monitoring Assessment',
    // A2.3 - hidden for GEN profile
    req_field_label: '',
    req_field_placeholder: '',
    req_section_label: '',
    req_key_control_q: '',
    req_source_hint: '',
    req_input_mode: '',
    req_hidden: true,
    // A2.4
    performer_label: 'Control Performer',
    monitor_label: 'Monitor',
    facilitator_label: 'Facilitator (Design Assessor)',
    sod_label: 'Segregation of duties',
    // A2.5
    product_label: 'Scope',
    product_q_label: 'Does the Control cover the applicable scope?',
    segment_label: 'Population',
    channel_label: 'Channel',
    // A2.6
    evidence_label: 'Evidence',
    evidence_types_hint: 'Process log, approval record, exception report, peer review',
    instructions_label: 'Procedures / instructions',
    escalation_label: 'Escalation process',
    // A2.7
    rating_adequate: 'Adequate',
    rating_inadequate: 'Inadequate',
    issue_label: 'Issue',
    threshold_label: 'Pass / fail threshold',
    monitoring_type_default: 'Either (detect from description)',
  },
};
